// Screen 2.1 — Breakdown expired/closed gate (Figma 1-500).
// Shared destination: the deadline gate, post-flow "Explore more
// opportunities" and win-back returns all land here — stateless/param-driven.
// Reads the breakdown name from router state, falling back to a re-fetch by
// ?breakdownId=, falling back to generic copy.
//
// Reached right after a purchase (the gate passes paid in router state, or the
// URL still says paid=1), it is the "closed after payment" outcome instead: the
// plan started and is kept, and the way on is another casting call (docs/30 §3).

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import i18next from "i18next";
import {
  getSubmissionFlowApi,
  ISubmissionMembershipDto,
} from "../../../shared/api/submission";
import { parseSubmissionParams } from "../common/submissionParams";
import {
  fallbackTo,
  LANDING_BREAKDOWNS,
  LANDING_PARAM,
} from "../common/flowGuard";
import SubmissionLayout from "../common/SubmissionLayout";
import SubmissionOutcome from "../common/SubmissionOutcome";
import { isPaidReturn } from "../common/membershipPaywall";
import { goToDashboard } from "../common/dashboard";
import { getBreakdownInfo } from "../common/breakdownInfo";
import { trackSubmissionEvent } from "../analytics";
import { isSubmissionAuthed } from "../../../helpers/submissionAuth";
import { membershipPrimary } from "../common/membershipButton";

interface Props {
  history: any;
  location: any;
}

const BreakdownClosedPage: React.FC<Props> = ({ history, location }) => {
  // Never parsed: refs are zero-padded strings (bd=0175445).
  const { bd: breakdownId } = parseSubmissionParams(location.search);
  const stateName =
    location.state && location.state.breakdownName
      ? (location.state.breakdownName as string)
      : null;

  const stateReason =
    location.state && location.state.closedReason
      ? (location.state.closedReason as string)
      : null;

  const [breakdownName, setBreakdownName] = useState<string | null>(stateName);
  // "allRolesCast" is its own kind of closed: the project is still running,
  // there is just nothing left to submit to. Everything else — expired, taken
  // down, unavailable, unknown — reads as "you just missed this one".
  const [closedReason, setClosedReason] = useState<string | null>(stateReason);
  // Someone who made their account in this flow (or came back signed in) has
  // nothing to create and nobody to log in as: their way on is another casting
  // call, or their dashboard.
  const signedIn = isSubmissionAuthed();
  const paid =
    signedIn && ((location.state && !!location.state.paid) || isPaidReturn(location.search));
  // For the plan line; the plan may still be on its way, and then there's none.
  const [membership, setMembership] = useState<ISubmissionMembershipDto | null>(null);

  useEffect(() => {
    trackSubmissionEvent("submission_breakdown_closed_shown", {
      breakdownId,
      source: stateName ? "gate" : "deeplink",
      closedReason: stateReason,
      paid,
    });
    if (paid) {
      getSubmissionFlowApi()
        .getSubmissionMembership()
        .then(setMembership)
        .catch(() => undefined);
    }
    if ((!stateName || !stateReason) && breakdownId) {
      // Best effort — generic copy if this fails; never block the page.
      getBreakdownInfo(breakdownId)
        .then((info) => {
          setBreakdownName(info.breakdownName);
          if (info.closedReason) setClosedReason(info.closedReason);
        })
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The flow needs a breakdown to submit to, and this page is where a user
  // lands when theirs is gone — so "find more roles" hands them to the
  // standard actor signup rather than a wizard with no target.
  const onCreateAccount = () => {
    trackSubmissionEvent("sf_closed_to_signup", { breakdownId });
    // page=breakdowns keeps the promise this button makes: the new account
    // lands on breakdowns, not on a generic dashboard. The visitor came here
    // because the job they wanted was gone — dropping them anywhere else is
    // the second dead end in a row.
    history.replace(
      fallbackTo(location.search, { [LANDING_PARAM]: LANDING_BREAKDOWNS })
    );
  };

  const loginUrl = `${process.env.REACT_APP_CWB_Actor_Login}${location.search}`;
  const allCast = closedReason === "allRolesCast";
  // The account already has a submission with casting for this breakdown, any
  // role (one per actor per casting call). Not closed: done.
  const alreadySubmitted = new URLSearchParams(location.search).get("reason") === "submitted";

  const onSeeCastingCalls = () => history.push("/submission/casting-calls");
  const onDashboard = () => {
    trackSubmissionEvent("sf_closed_to_dashboard", { breakdownId, paid });
    goToDashboard(history);
  };

  if (paid) {
    const status = membership ? membership.membershipStatus : null;
    return (
      <SubmissionLayout>
        <SubmissionOutcome
          kind="closedAfterPayment"
          plan={status === "trial" || status === "pro" ? status : null}
          trialEndsUtc={membership ? membership.planEndsUtc : null}
          onDashboard={onDashboard}
          onExplore={onSeeCastingCalls}
          onSeeCastingCalls={onSeeCastingCalls}
          // No Pro offer after a payment: the offer is for who submitted free.
          onGoPro={() => undefined}
        />
      </SubmissionLayout>
    );
  }

  return (
    <SubmissionLayout>
      <Centered>
      <Emoji aria-hidden="true">{alreadySubmitted ? "✅" : "🎬"}</Emoji>
      <Title>
        {alreadySubmitted
          ? breakdownName
            ? i18next.t("You've already submitted to {{breakdownName}}", { breakdownName })
            : i18next.t("You've already submitted to this casting call")
          : allCast
          ? breakdownName
            ? i18next.t("Every role in {{breakdownName}} has been cast", { breakdownName })
            : i18next.t("Every role here has been cast")
          : breakdownName
          ? i18next.t("{{breakdownName}} is closed", { breakdownName })
          : i18next.t("This casting call is closed")}
      </Title>
      <Message>
        {alreadySubmitted
          ? i18next.t("Casting has your submission. Each casting call takes one submission per actor.")
          : allCast
          ? i18next.t("The project is still going, but casting has found everyone they need.")
          : i18next.t("You just missed this one.")}
        <br />
        {i18next.t("Lots more roles opened this week.")}
      </Message>
      {signedIn || alreadySubmitted ? (
        <>
          <PrimaryButton onClick={onSeeCastingCalls}>
            {i18next.t("See open casting calls")}
          </PrimaryButton>
          <BrowseLink onClick={onDashboard}>{i18next.t("Go to my dashboard")}</BrowseLink>
        </>
      ) : (
        <>
          <PrimaryButton onClick={onCreateAccount}>
            {i18next.t("Create my account & find more roles")}
          </PrimaryButton>
          <BrowseLink onClick={() => history.push("/submission/casting-calls")}>
            {i18next.t("See open casting calls")}
          </BrowseLink>
          <LoginRow>
            <span>{i18next.t("Already in theWorkbook?")}</span>
            <LoginLink href={loginUrl}>{i18next.t("Log in")}</LoginLink>
          </LoginRow>
        </>
      )}
      </Centered>
    </SubmissionLayout>
  );
};

// Vertically centred on mobile, where this is the whole screen; on desktop the
// layout's column already positions it under the wordmark.
const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: calc(100vh - 120px);
  padding: 24px 0;

  @media all and (min-width: 1024px) {
    min-height: 0;
    justify-content: flex-start;
    padding: 48px 0 0;
  }
`;

const Emoji = styled.div`
  font-size: 44px;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 12px;
  max-width: 340px;
  overflow-wrap: break-word;

  /* The 340px measure keeps a long breakdown name from spanning the whole phone
     column; on a wide screen it just forces needless wrapping. */
  @media all and (min-width: 1024px) {
    max-width: 100%;
  }
`;

const Message = styled.p`
  font-size: 15px;
  color: #53627c;
  line-height: 22px;
  margin: 0 0 28px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  max-width: 360px;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

const BrowseLink = styled.button`
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  margin-top: 16px;
  cursor: pointer;
`;

const LoginRow = styled.div`
  margin-top: 32px;
  font-size: 13px;
  color: #53627c;
`;

const LoginLink = styled.a`
  color: #0f9bf0;
  font-weight: 700;
  margin-left: 6px;
  text-decoration: none;
`;



export default BreakdownClosedPage;
